import React from "react";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	CardinalLocation,
	ClassNameProps,
	Column,
	dateDiff,
	Grid,
	Heading,
	If,
	isAfter,
	isBefore,
	isSameTime,
	makeClassName,
	mappedArray,
	Modal,
	Row,
	StyleProps,
	toLocalDateString,
	Tooltip,
	toTime,
	TruncatedText,
	useClickOutListener,
	useInterval,
	useSync,
} from "@hex-insights/core";
import { CalendarEventFormat } from "../../../../Models/Format";
import { truncateDisplayItems } from "../../../../Utilities";
import { Icon } from "../../../Icon";
import { EventCalendarItem, EventTooltipProps } from "../../types";
import { getEventColor } from "../../utils";
import { DefaultEventTooltip } from "../Tooltip";
import styles from "./styles.module.css";

export type TimedViewProps = {
	values: Date[];
	events?: EventCalendarItem[] | NodeEdge<EventCalendarItem>[];
	dateEventMap: Record<string, EventCalendarItem[]>;
	hideDateInDayLabel?: boolean;
	noItemIcon?: boolean;
	detailModalVariant?: "default" | "admin";
} & Pick<TimedViewEventProps, "EventTooltip"> &
	Partial<ClassNameProps & StyleProps>;

export function TimedView({
	values,
	dateEventMap,
	EventTooltip,
	hideDateInDayLabel = false,
	noItemIcon,
	detailModalVariant = "default",
	className,
	style,
}: TimedViewProps) {
	const value = values[0];
	const todayIndex = values.findIndex((value) => isSameTime(new Date(), value, "day"));
	const valuesIncludesToday = todayIndex !== -1;

	const anyAllDayEvents = React.useMemo(
		() =>
			values
				.map((value) => dateEventMap[toLocalDateString(value)] ?? [])
				.some((events) => events.some((event) => event.isAllDay)),
		[values, dateEventMap],
	);

	const hourMarks = React.useMemo(() => mappedArray(24, (i) => addTimeToDate(value, [i, "hours"]).toDate()), [value]);

	return (
		<Column
			className={makeClassName(
				styles["timed-view"],
				values.length === 1 ? styles["timed-view--single-day"] : styles["timed-view--multi-day"],
				className,
			)}
			style={style}
		>
			<If condition={values.length > 1}>
				<Grid tracks={values.length} className={styles["timed-view__day-labels"]}>
					{values.map((value) => (
						<TimedViewDayLabel key={value.toISOString()} value={value} hideDate={hideDateInDayLabel} />
					))}
				</Grid>
			</If>

			<Column className={makeClassName(styles["timed-view__content"])}>
				<If condition={anyAllDayEvents}>
					<Row className={styles["timed-view__all-day-events"]}>
						<div className={styles["timed-view__hour-labels"]}>
							<div className={styles["timed-view__hour-labels__label"]}>
								<span className={styles["timed-view__hour-labels__label__text"]}>All Day</span>
							</div>
						</div>

						<div className={styles["timed-view__events-columns-grid__wrapper"]}>
							<Grid tracks={values.length} className={styles["timed-view__events-columns-grid"]}>
								{values.map((value) => (
									<TimedViewAllDayEventsColumn
										key={value.toISOString()}
										value={value}
										events={dateEventMap[toLocalDateString(value)] ?? []}
										EventTooltip={EventTooltip}
										noIcon={noItemIcon}
										detailModalVariant={detailModalVariant}
									/>
								))}
							</Grid>

							<If condition={values.length > 1}>
								<DayMarks values={values} />
							</If>
						</div>
					</Row>
				</If>

				<div style={{ overflow: "hidden scroll" }}>
					<Row className={styles["timed-view__timed-events"]}>
						<Column className={styles["timed-view__hour-labels"]}>
							{hourMarks.map((hour) => {
								const formattedHour = hour.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
								return (
									<div key={formattedHour} className={styles["timed-view__hour-labels__label"]}>
										<span className={styles["timed-view__hour-labels__label__text"]}>{formattedHour}</span>
									</div>
								);
							})}
						</Column>

						<div className={styles["timed-view__events-columns-grid__wrapper"]}>
							<Grid tracks={values.length} className={styles["timed-view__events-columns-grid"]}>
								{values.map((value) => (
									<TimedViewTimedEventsColumn
										key={value.toISOString()}
										value={value}
										events={dateEventMap[toLocalDateString(value)] ?? []}
										EventTooltip={EventTooltip}
										noIcon={noItemIcon}
										detailModalVariant={detailModalVariant}
									/>
								))}
							</Grid>

							<If condition={valuesIncludesToday}>
								<CurrentTimeMark todayIndex={todayIndex} numDays={values.length} />
							</If>

							<HourMarks hourMarks={hourMarks} />

							<If condition={values.length > 1}>
								<DayMarks values={values} />
							</If>
						</div>
					</Row>
				</div>
			</Column>
		</Column>
	);
}

type TimedViewDayLabelProps = {
	value: Date;
	hideDate?: boolean;
};

function TimedViewDayLabel({ value, hideDate = false }: TimedViewDayLabelProps) {
	const isToday = isSameTime(new Date(), value, "day");

	return (
		<Column
			key={value.toISOString()}
			align="center"
			className={makeClassName(
				styles["timed-view__day-labels__label"],
				isToday ? styles["timed-view__day-labels__label--today"] : "",
			)}
		>
			<span className={styles["timed-view__day-labels__label__dow"]}>
				{value.toLocaleString(undefined, { weekday: "short" })}
			</span>
			<If condition={!hideDate}>
				<span className={styles["timed-view__day-labels__label__day"]}>
					{value.toLocaleString(undefined, { day: "numeric" })}
				</span>
			</If>
		</Column>
	);
}

type DayMarksProps = {
	values: Date[];
};

function DayMarks({ values }: DayMarksProps) {
	return (
		<div className={styles["timed-view__day-marks"]}>
			{values.map((_, i) => {
				if (i === values.length - 1) {
					return null;
				}

				return (
					<div
						key={i}
						className={styles["timed-view__day-mark"]}
						style={{ "--mark-day": i, "--num-days": values.length } as React.CSSProperties}
					></div>
				);
			})}
		</div>
	);
}

type CurrentTimeMarkProps = {
	todayIndex: number;
	numDays: number;
};

function CurrentTimeMark({ todayIndex, numDays }: CurrentTimeMarkProps) {
	const ref = React.useRef<HTMLDivElement | null>(null);
	React.useEffect(() => {
		ref.current?.scrollIntoView();
	}, []);

	const [hours, setHours] = React.useState(() => toTime("hour"));
	const { setInterval } = useInterval();
	React.useEffect(() => {
		setInterval(() => {
			setHours(toTime("hour"));
		}, 60 * 1000);
	}, [setInterval]);

	return (
		<div
			ref={ref}
			className={styles["timed-view__events-column__current-time-mark"]}
			style={{ "--mark-hour": hours, "--today-index": todayIndex, "--num-days": numDays } as React.CSSProperties}
		></div>
	);
}

type HourMarksProps = {
	hourMarks: Date[];
};

function HourMarks({ hourMarks }: HourMarksProps) {
	return (
		<React.Fragment>
			{hourMarks.map((_, i) => (
				<div
					key={i}
					className={styles["timed-view__events-column__hour-mark"]}
					style={{ "--mark-hour": i } as React.CSSProperties}
				></div>
			))}
		</React.Fragment>
	);
}

type TimedViewAllDayEventsColumnProps = {
	value: Date;
	events: EventCalendarItem[];
	noIcon?: boolean;
	detailModalVariant?: "default" | "admin";
} & Pick<TimedViewEventProps, "EventTooltip">;

function TimedViewAllDayEventsColumn({
	value,
	events: allEvents,
	detailModalVariant = "default",
	EventTooltip,
	noIcon,
}: TimedViewAllDayEventsColumnProps) {
	const events = allEvents.filter((event) => event.isAllDay);

	const {
		displayedItems: displayedEvents,
		anyHidden: anyEventsHidden,
		numHidden: numHiddenEvents,
	} = truncateDisplayItems(events, 2);

	const {
		isModalOpen: isAllDayEventsModalOpen,
		openModal: openAllDayEventsModal,
		closeModal: closeAllDayEventsModal,
	} = Modal.useToggle();

	return (
		<div className={styles["timed-view__events-column"]}>
			<Column justify="spaced-start" verticalSpacing="0.25rem" className={styles["timed-view__events-column__content"]}>
				{displayedEvents.map((calendarEvent) => (
					<TimedViewEvent
						key={calendarEvent.id}
						calendarEvent={calendarEvent}
						dayDate={value}
						EventTooltip={EventTooltip}
						noIcon={noIcon}
						modalVariant={detailModalVariant}
					/>
				))}
				<If condition={anyEventsHidden}>
					<Button
						size="small"
						variant="link"
						onClick={openAllDayEventsModal}
						className={styles["timed-view__show-more-button"]}
					>
						+ {numHiddenEvents} More
					</Button>
				</If>
				<Modal.If condition={isAllDayEventsModalOpen}>
					<Modal onClose={closeAllDayEventsModal} className={styles["timed-view__events-modal"]}>
						<Modal.Header>
							<Column justify="spaced-start">
								<Button variant="link" size="small" onClick={closeAllDayEventsModal}>
									<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
										<Icon.ArrowLeft size={14} /> Back to Calendar
									</Row>
								</Button>

								<Heading.H2 noMargin>
									All Day:{" "}
									{value.toLocaleString(undefined, {
										year: "numeric",
										month: "short",
										day: "numeric",
										weekday: "long",
									})}
								</Heading.H2>
							</Column>
						</Modal.Header>
						<Modal.Body>
							<Column justify="spaced-start" verticalSpacing="0.25rem">
								{events.map((calendarEvent) => (
									<TimedViewEvent
										key={calendarEvent.id}
										calendarEvent={calendarEvent}
										dayDate={value}
										EventTooltip={EventTooltip}
										noIcon={noIcon}
										modalVariant={detailModalVariant}
									/>
								))}
							</Column>
						</Modal.Body>
					</Modal>
				</Modal.If>
			</Column>
		</div>
	);
}

type TimedViewTimedEventsColumnProps = {
	value: Date;
	events: EventCalendarItem[];
	noIcon?: boolean;
	detailModalVariant?: "default" | "admin";
} & Pick<TimedViewEventProps, "EventTooltip">;

function TimedViewTimedEventsColumn({
	value,
	events: allEvents,
	EventTooltip,
	detailModalVariant = "default",
	noIcon,
}: TimedViewTimedEventsColumnProps) {
	const events = allEvents.filter((event) => !event.isAllDay);

	return (
		<div className={styles["timed-view__events-column"]}>
			<div className={styles["timed-view__events-column__content"]}>
				{events.map((calendarEvent) => (
					<TimedViewEvent
						key={calendarEvent.id}
						calendarEvent={calendarEvent}
						dayDate={value}
						EventTooltip={EventTooltip}
						modalVariant={detailModalVariant}
						noIcon={noIcon}
					/>
				))}
			</div>
		</div>
	);
}

const eventTooltipSideOrder: CardinalLocation[] = ["right", "left", "bottom", "top"];

type TimedViewEventProps = {
	calendarEvent: EventCalendarItem;
	dayDate: Date;
	EventTooltip?: (props: EventTooltipProps) => JSX.Element | null;
	modalVariant?: "default" | "admin";
	noIcon?: boolean;
};

function TimedViewEvent({
	calendarEvent,
	EventTooltip = DefaultEventTooltip,
	modalVariant = "default",
	dayDate,
	noIcon = false,
}: TimedViewEventProps) {
	const eventColor = getEventColor(calendarEvent);

	const { isTooltipOpen, openTooltip, closeTooltip } = Tooltip.useToggle();
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const ref = React.useRef<HTMLDivElement | null>(null);
	useClickOutListener(ref.current, closeTooltip, isTooltipOpen && !isModalOpen);
	const { syncID, synchronize } = useSync();

	let durationHours = 1;
	let startTimeHours = 0;
	let timedEventStartsBeforeDay = false;
	let timedEventEndsAfterDay = false;
	if (!calendarEvent.isAllDay && calendarEvent.startTime !== null && calendarEvent.endTime !== null) {
		const dayStartTime = dayDate;
		timedEventStartsBeforeDay = isBefore(calendarEvent.startTime, dayStartTime);
		const startTime = timedEventStartsBeforeDay ? dayStartTime : calendarEvent.startTime;
		const dayEndTime = addTimeToDate(dayDate, [1, "day"]).toDate();
		timedEventEndsAfterDay = isAfter(calendarEvent.endTime, dayEndTime);
		const endTime = timedEventEndsAfterDay ? dayEndTime : calendarEvent.endTime;

		durationHours = dateDiff(endTime, startTime, "hours", false);
		startTimeHours = toTime("hour", new Date(startTime));
	}

	return (
		<div
			ref={ref}
			className={makeClassName(
				styles["timed-view__event"],
				isTooltipOpen ? styles["timed-view__event--tooltip-open"] : "",
				calendarEvent.isAllDay ? styles["timed-view__event--all-day"] : styles["timed-view__event--timed"],
				timedEventStartsBeforeDay ? styles["timed-view__event--starts-before-day"] : "",
				timedEventEndsAfterDay ? styles["timed-view__event--ends-after-day"] : "",
			)}
			style={
				{
					"--event-color": eventColor,
					"--event-duration-hours": durationHours,
					"--event-start-time-hours": startTimeHours,
				} as React.CSSProperties
			}
		>
			<Tooltip.Container
				isControlled
				isOpen={isTooltipOpen}
				contentSyncID={syncID}
				sideOrder={eventTooltipSideOrder}
				style={{ width: "100%", overflow: "visible" }}
			>
				<EventTooltip
					item={calendarEvent}
					isOpen={isTooltipOpen}
					synchronizeContent={synchronize}
					withModalBackButton
					setIsModalOpen={setIsModalOpen}
					noStem
					modalVariant={modalVariant}
					className={styles["timed-view__event__tooltip"]}
				/>

				<div
					onClick={openTooltip}
					className={makeClassName(
						styles["timed-view__event__tile"],
						durationHours < 1 ? styles["timed-view__event__tile--short"] : "",
					)}
				>
					<Row justify="space-between" align="center" className={styles["timed-view__event__tile__content"]}>
						<Row justify="spaced-start" align="center" style={{ overflow: "hidden" }}>
							<If condition={!noIcon}>
								<Icon.CalendarBlank size={16} color={eventColor} />
							</If>

							<Column style={{ overflow: "hidden" }}>
								<TruncatedText
									className={styles["timed-view__event__tile__name"]}
									style={{ position: "relative", zIndex: 1 }}
								>
									{CalendarEventFormat.name(calendarEvent)}
								</TruncatedText>

								<TruncatedText
									className={
										styles["timed-view__event__tile__time"] + " " + styles["timed-view__event__tile__time--column"]
									}
								>
									{CalendarEventFormat.timeRangeOnDate(calendarEvent, dayDate)}
								</TruncatedText>
							</Column>
						</Row>

						<TruncatedText
							className={styles["timed-view__event__tile__time"] + " " + styles["timed-view__event__tile__time--row"]}
						>
							{CalendarEventFormat.timeRangeOnDate(calendarEvent, dayDate)}
						</TruncatedText>
					</Row>
				</div>
			</Tooltip.Container>
		</div>
	);
}
